/**
 * MUSIQUE — la politique, pure et testable (ROADMAP 12.9, E§4).
 *
 * ══ Pourquoi PAS un fichier de musique ══════════════════════════════════════
 *
 * Il n'y en a aucun dans le dépôt, et c'est ce qui a bloqué ce lot depuis le premier
 * jour. Mais même avec un fichier, ce serait le mauvais choix — et c'est la vraie raison
 * de ce module.
 *
 * **Un idle se joue pendant des heures.** Une boucle de deux minutes, entendue soixante
 * fois, cesse d'être une ambiance et devient une contrainte : le joueur coupe le son, et
 * l'identité sonore du §4 disparaît avec. Le seul moyen de tenir la durée est une musique
 * qui ne se répète jamais exactement.
 *
 * On la GÉNÈRE donc, note par note. C'est la même décision que la pentatonique de
 * `soundPolicy.ts`, poussée d'un cran : ce n'est pas un choix esthétique mais une
 * propriété — **une gamme sans seconde mineure n'a aucune paire de notes dissonante**,
 * donc une suite tirée au hasard dedans sonne juste par construction. On ne compose pas,
 * on choisit un espace où toutes les compositions sont acceptables.
 *
 * ══ Pourquoi ce fichier est PUR ═════════════════════════════════════════════
 *
 * Aucun `AudioContext`, aucun `Date.now()`, aucun aléa non injecté. Il rend des NOMBRES —
 * quelle note, à quel volume, pendant combien de temps. `audio/music.ts` n'est que la
 * couche Web Audio qui obéit.
 *
 * La conséquence est celle du §4a : on peut vérifier l'harmonie, la densité et la réponse
 * au cycle du jour **sans navigateur et sans écouter**. Un test peut affirmer « aucune
 * seconde mineure ne sort jamais » ; une oreille ne le peut pas.
 */

import { PENTATONIC_SEMITONES } from './soundPolicy';

/**
 * Fréquence de la fondamentale, en hertz.
 *
 * La2 = 220 Hz, une octave sous le la du diapason. Assez grave pour rester un fond —
 * l'aigu attire l'oreille, et une musique d'ambiance qui attire l'oreille a échoué.
 */
export const MUSIC_ROOT_HZ = 220;

/** Rapport de fréquence d'un demi-ton. */
const SEMITONE = 2 ** (1 / 12);

/**
 * Degrés disponibles, sur deux octaves et demie.
 *
 * ⚠️ La table est CONSTRUITE depuis `PENTATONIC_SEMITONES`, jamais énumérée à la main.
 * Écrire les quinze intervalles serait une occasion de se tromper d'un demi-ton — et un
 * demi-ton faux dans une gamme sans seconde mineure crée exactement la dissonance que la
 * gamme existe pour interdire (règle 15).
 */
export const MUSIC_DEGREES: readonly number[] = Array.from({ length: 3 }, (_, octave) =>
  PENTATONIC_SEMITONES.map((semitone) => semitone + octave * 12),
).flat();

/** Fréquence d'un degré, en hertz. */
export function degreeToHz(semitones: number): number {
  return MUSIC_ROOT_HZ * SEMITONE ** semitones;
}

/**
 * Ce qu'une note demande à la couche audio.
 *
 * `delayMs` compte depuis l'instant du calcul : la politique ne connaît pas l'horloge, on
 * lui dit quand elle est appelée et elle répond en durées relatives. C'est la règle du
 * §4a — le temps est FOURNI au moteur, jamais lu par lui.
 */
export interface MusicNote {
  readonly hz: number;
  readonly delayMs: number;
  readonly durationMs: number;
  readonly gain: number;
}

/**
 * Densité et hauteur selon le moment de la journée.
 *
 * ⚠️ `phase` vaut 0 à minuit, 0,5 à midi — la même convention que `dayCycle.ts`. Ne pas
 * la redéfinir ici : deux conventions d'horloge dans un même jeu finissent toujours par
 * se croiser, et le jour se lèverait en musique de nuit.
 *
 * Le jour est plus dense et plus clair, la nuit plus rare et plus grave. Ce n'est pas un
 * effet : c'est ce qui fait qu'une ville jouée à trois heures du matin ne sonne pas comme
 * la même ville à midi, sans qu'on ait à composer deux morceaux.
 */
export function moodAt(phase: number): { readonly density: number; readonly lift: number } {
  // Distance à midi, normalisée : 0 en plein jour, 1 en pleine nuit.
  const night = Math.abs((((phase % 1) + 1) % 1) - 0.5) * 2;
  return {
    density: 1 - night * 0.55,
    // Décalage de registre, en degrés de la gamme. La nuit descend, jamais l'inverse.
    lift: Math.round((1 - night) * 4),
  };
}

/**
 * Compose la prochaine mesure.
 *
 * ══ Les trois règles qui rendent le hasard écoutable ═══════════════════════
 *
 * 1. **La marche est petite.** La note suivante ne s'éloigne jamais de plus de deux
 *    degrés de la précédente : c'est ce qui donne une MÉLODIE plutôt qu'une suite de
 *    notes. Un tirage uniforme dans la gamme sonne juste et n'a aucune direction.
 *
 * 2. **Le silence compte autant que la note.** À densité pleine on ne joue qu'une case
 *    sur deux ; la nuit, une sur quatre. Une musique d'ambiance qui remplit tout le temps
 *    devient un bourdon, et le joueur la coupe — c'est le mode d'échec le plus fréquent
 *    d'une musique générative.
 *
 * 3. **Aucune note ne se répète immédiatement.** Deux fois la même hauteur d'affilée
 *    s'entend comme un bégaiement, là où un intervalle passe inaperçu.
 *
 * @param from   degré de la dernière note jouée
 * @param phase  moment de la journée, 0 = minuit
 * @param beats  combien de temps la mesure porte
 * @param random source d'aléa, injectée pour que les tests soient déterministes
 */
export function composeBar(
  from: number,
  phase: number,
  beats: number,
  random: () => number,
): { readonly notes: readonly MusicNote[]; readonly last: number } {
  const { density, lift } = moodAt(phase);
  const notes: MusicNote[] = [];
  let current = from;

  for (let beat = 0; beat < beats; beat++) {
    if (random() > density * 0.5) continue;

    /*
      ── LA MARCHE REBONDIT, ELLE NE SE PLAFONNE PAS ────────────────────────

      ⚠️ Première rédaction : `Math.max(0, Math.min(max, current + step))`. Le
      plafonnement écrase DEUX positions différentes sur la même note dès qu'on touche
      un bord — et la règle « aucune note ne se répète d'affilée » tombe précisément là
      où la mélodie est déjà la plus pauvre, en haut ou en bas du registre.

      Le test l'a vu à la note près : « expected 369.99 not to be 369.99 ». À l'oreille
      ç'aurait été un bégaiement occasionnel, impossible à imputer.

      On INVERSE donc le pas quand il sortirait, au lieu de le raboter. La mélodie
      repart vers l'intérieur, et deux notes consécutives restent toujours distinctes.

      Le sommet dépend du décalage de registre : la fenêtre utilisable se rétrécit quand
      la journée monte la musique, pour que la transposition reste une transposition et
      non un nouvel écrasement.
    */
    const top = MUSIC_DEGREES.length - 1 - lift;
    const steps = [-2, -1, 1, 2];
    const step = steps[Math.floor(random() * steps.length)] ?? 1;
    let next = current + step;
    if (next < 0 || next > top) next = current - step;
    // Fenêtre plus étroite que le pas : on se rabat sur le voisin immédiat, qui existe
    // toujours dès que la fenêtre porte au moins deux degrés.
    if (next < 0 || next > top) next = current > 0 ? current - 1 : current + 1;
    current = Math.max(0, Math.min(top, next));
    const index = current;

    /*
      ⚠️ LE DÉCALAGE EST EN DEGRÉS DE LA GAMME, JAMAIS EN DEMI-TONS.

      Première rédaction : `MUSIC_DEGREES[index] + lift`, avec `lift` de 0 à 4. Trois
      demi-tons ajoutés à un degré pentatonique donnent une note **hors gamme** — donc
      des secondes mineures, donc exactement la dissonance que le choix de la gamme
      existe pour rendre impossible.

      Le commentaire de `moodAt` disait pourtant « en degrés de la gamme ». Le code
      faisait autre chose, et rien ne les confrontait : c'est `tests/musicPolicy.test.ts`
      qui l'a vu, en remontant du hertz au degré sur cinq cents mesures. Une oreille
      aurait entendu « c'est un peu bizarre le matin » sans savoir dire pourquoi.
    */
    const degree = MUSIC_DEGREES[index + lift] ?? 0;
    notes.push({
      hz: degreeToHz(degree),
      delayMs: beat * BEAT_MS,
      // Longue et se recouvrant : c'est le recouvrement qui fait un tapis plutôt qu'une
      // suite de bips. Une note plus courte que l'intervalle laisse un trou audible.
      durationMs: BEAT_MS * 2.4,
      // Les aigus portent plus loin à volume égal : on les rentre, sinon la mélodie
      // sort du fond dès qu'elle monte.
      gain: 0.5 - Math.min(0.25, degree / 96),
    });
  }

  return { notes, last: current };
}

/**
 * Durée d'un temps.
 *
 * 1,2 s — soit 50 battements par minute, sous le rythme cardiaque au repos. Une musique
 * plus rapide qu'un cœur presse le joueur ; un idle ne presse personne.
 */
export const BEAT_MS = 1200;

/** Combien de temps par mesure. Huit temps ≈ dix secondes de musique par calcul. */
export const BEATS_PER_BAR = 8;
